'use client';

import { useEffect, useRef, useState } from 'react';
import { LuCheck, LuCrown, LuBuilding2, LuHardDrive, LuMic, LuUsers, LuBookOpen } from 'react-icons/lu';
import { Badge } from '@core/ui/primitives/badge';
import { Button } from '@core/ui/primitives/button';
import { Spinner } from '@core/ui/primitives/spinner';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useScrolling } from '@core/ui/hooks/use-scrolling';

interface TierInfo {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  features: string[]; // Marketing highlights (display strings)
  entitlements?: string[]; // Feature IDs for enforcement (e.g., 'search:semantic', 'comments')
  limits: {
    maxStorageMB: number;
    maxTranscriptionMinutesPerMonth: number;
  };
  isCurrent: boolean;
  comingSoon: boolean;
}

interface SubscriptionData {
  tier: {
    id: string | null;
    slug: string;
    name: string;
    description: string | null;
  };
  limits: {
    maxStorageMB: number;
    maxTranscriptionMinutesPerMonth: number;
  } | null;
  availableTiers: TierInfo[];
  usage: {
    storage: {
      usedBytes: string;
      usedMB: number;
      limitMB: number;
      percent: number;
      isAtWarning: boolean;
      isAtLimit: boolean;
    };
    transcription: {
      usedMinutes: number;
      limitMinutes: number;
      percent: number;
      isAtWarning: boolean;
      isAtLimit: boolean;
    };
    vaultCount: number;
    memberCount: number;
  };
  billingCycle: {
    start: string;
    end: string;
    anchor: string;
  };
}

interface OrganizationSubscriptionPanelProps {
  organizationId: string;
  onReady?: (opts?: { immediate?: boolean }) => void;
}

function formatMB(mb: number): string {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`;
  }
  return `${mb.toFixed(0)} MB`;
}

function UsageBar({
  label,
  icon: Icon,
  used,
  limit,
  percent,
  isAtWarning,
  isAtLimit,
  formatUsed,
  formatLimit,
}: {
  label: string;
  icon: React.ElementType;
  used: number;
  limit: number;
  percent: number;
  isAtWarning: boolean;
  isAtLimit: boolean;
  formatUsed: (value: number) => string;
  formatLimit: (value: number) => string;
}) {
  const barColor = isAtLimit
    ? 'bg-destructive'
    : isAtWarning
      ? 'bg-amber-500'
      : 'bg-brand';

  return (
    <div className="org-subscription-usage-bar space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{formatUsed(used)}</span>
          <span className="text-muted-foreground">/ {limit > 0 ? formatLimit(limit) : 'Unlimited'}</span>
          {isAtLimit && (
            <Badge variant="destructive" className="text-xs">
              Limit reached
            </Badge>
          )}
          {isAtWarning && !isAtLimit && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
              Near limit
            </Badge>
          )}
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all duration-300', barColor)}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
    </div>
  );
}

function getTierIcon(slug: string) {
  const normalizedSlug = slug.toLowerCase();
  if (normalizedSlug === 'free') return LuUsers;
  if (normalizedSlug === 'pro') return LuCrown;
  return LuBuilding2;
}

function getTierIconStyle(slug: string) {
  const normalizedSlug = slug.toLowerCase();
  if (normalizedSlug === 'free') return { bg: 'bg-muted', icon: 'text-muted-foreground' };
  if (normalizedSlug === 'pro') return { bg: 'bg-primary/10', icon: 'text-primary' };
  return { bg: 'bg-amber-500/10', icon: 'text-amber-600' };
}

// Map entitlement IDs to human-readable labels
const ENTITLEMENT_LABELS: Record<string, string> = {
  'search:basic': 'Basic Search',
  'search:semantic': 'Semantic Search (AI)',
  'transcription': 'Audio Transcription',
  'comments': 'Comments & Discussions',
  'mindmap': 'Mind Maps',
  'excalidraw': 'Excalidraw Diagrams',
};

// Sort order for entitlements - groups related features together
const ENTITLEMENT_ORDER: Record<string, number> = {
  'search:basic': 1,
  'search:semantic': 2,
  'transcription': 10,
  'comments': 20,
  'mindmap': 30,
  'excalidraw': 31,
};

function sortEntitlements(entitlements: string[]): string[] {
  return [...entitlements].sort((a, b) => {
    const orderA = ENTITLEMENT_ORDER[a] ?? 100;
    const orderB = ENTITLEMENT_ORDER[b] ?? 100;
    return orderA - orderB;
  });
}

// Get entitlement label with quota if applicable
function getEntitlementLabel(entitlement: string, limits: TierInfo['limits']): string {
  const baseLabel = ENTITLEMENT_LABELS[entitlement] || entitlement;

  // Add quota info for features that have limits
  if (entitlement === 'transcription' && limits.maxTranscriptionMinutesPerMonth > 0) {
    return `${baseLabel} (${limits.maxTranscriptionMinutesPerMonth} min/month)`;
  }

  return baseLabel;
}

// Format storage for display
function formatStorageLimit(mb: number): string {
  if (mb <= 0) return 'Unlimited';
  if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
  return `${mb} MB`;
}

function TierCard({ tier }: { tier: TierInfo }) {
  const Icon = getTierIcon(tier.slug);
  const iconStyle = getTierIconStyle(tier.slug);

  return (
    <div
      className={cn(
        'org-subscription-tier-card relative flex flex-col rounded-xl border p-6 transition-all',
        tier.isCurrent && 'border-brand/50 bg-brand/5',
        tier.comingSoon && 'opacity-75'
      )}>
      {tier.isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 rounded-full bg-brand text-black text-xs font-semibold tracking-wide">
            Current
          </span>
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg', iconStyle.bg)}>
          <Icon className={cn('h-5 w-5', iconStyle.icon)} />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{tier.name}</h3>
        </div>
      </div>

      {tier.description && (
        <p className="mb-4 text-sm text-muted-foreground">{tier.description}</p>
      )}

      <ul className="mb-6 flex-1 space-y-2">
        {/* Storage quota - always show */}
        <li className="flex items-start gap-2 text-sm">
          <LuCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span className="text-muted-foreground">
            Storage ({formatStorageLimit(tier.limits.maxStorageMB)})
          </span>
        </li>
        {/* Show enabled features from entitlements (enforced) with quotas */}
        {sortEntitlements(tier.entitlements ?? []).map((entitlement) => (
          <li key={entitlement} className="flex items-start gap-2 text-sm">
            <LuCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="text-muted-foreground">
              {getEntitlementLabel(entitlement, tier.limits)}
            </span>
          </li>
        ))}
        {/* Fallback to marketing features if no entitlements */}
        {(!tier.entitlements || tier.entitlements.length === 0) && tier.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2 text-sm">
            <LuCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>

      {!tier.isCurrent && (
        <Button variant="outline" disabled={tier.comingSoon} className="w-full">
          {tier.comingSoon ? 'Coming Soon' : 'Upgrade'}
        </Button>
      )}
    </div>
  );
}

export function OrganizationSubscriptionPanel({ organizationId, onReady }: OrganizationSubscriptionPanelProps) {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrolling = useScrolling(scrollRef);

  useEffect(() => {
    let cancelled = false;

    async function fetchSubscription() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/organizations/${organizationId}/subscription`, {
          cache: 'no-store',
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to load subscription data');
        }

        const payload = await response.json();
        if (!cancelled) {
          setData(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load subscription');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          onReady?.({ immediate: true });
        }
      }
    }

    fetchSubscription();

    return () => {
      cancelled = true;
    };
  }, [organizationId, onReady]);

  if (loading) {
    return (
      <div className="org-subscription-panel-loading flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-subscription-panel-error flex h-64 items-center justify-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const resetDate = new Date(data.billingCycle.end);

  return (
    <div
      ref={scrollRef}
      className={cn(
        'org-subscription-panel h-full overflow-y-auto scrollbar-fade p-1 space-y-8',
        isScrolling && 'is-scrolling'
      )}>
      {/* Current Usage Section */}
      <section className="org-subscription-usage space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Current Usage</h2>
          <p className="text-sm text-muted-foreground">
            Usage resets on {resetDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Storage Usage */}
          <div className="rounded-lg border p-4">
            <UsageBar
              label="Storage"
              icon={LuHardDrive}
              used={data.usage.storage.usedMB}
              limit={data.usage.storage.limitMB}
              percent={data.usage.storage.percent}
              isAtWarning={data.usage.storage.isAtWarning}
              isAtLimit={data.usage.storage.isAtLimit}
              formatUsed={formatMB}
              formatLimit={formatMB}
            />
          </div>

          {/* Transcription Usage */}
          <div className="rounded-lg border p-4">
            <UsageBar
              label="Transcription"
              icon={LuMic}
              used={data.usage.transcription.usedMinutes}
              limit={data.usage.transcription.limitMinutes}
              percent={data.usage.transcription.percent}
              isAtWarning={data.usage.transcription.isAtWarning}
              isAtLimit={data.usage.transcription.isAtLimit}
              formatUsed={(v) => `${v} min`}
              formatLimit={(v) => `${v} min`}
            />
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <LuBookOpen className="h-4 w-4" />
            <span>{data.usage.vaultCount} vault{data.usage.vaultCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <LuUsers className="h-4 w-4" />
            <span>{data.usage.memberCount} member{data.usage.memberCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </section>

      {/* Plans Comparison Section */}
      <section className="org-subscription-plans space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Plans</h2>
          <p className="text-sm text-muted-foreground">
            Choose the plan that best fits your team's needs
          </p>
        </div>

        <div className={cn(
          'grid gap-6',
          data.availableTiers.length === 1 && 'md:grid-cols-1',
          data.availableTiers.length === 2 && 'md:grid-cols-2',
          data.availableTiers.length >= 3 && 'md:grid-cols-3'
        )}>
          {data.availableTiers.map((tier) => (
            <TierCard key={tier.id} tier={tier} />
          ))}
        </div>
      </section>
    </div>
  );
}
